/**
 * BD: SCR-001 history dropdowns (履歴) + overlap validation on 登録.
 */

import dayjs from 'dayjs';

import { JA_JP } from '@/components/organisms/RA2NDatePicker/constants';
import { parseDate, parseFormat } from '@/components/organisms/RA2NDatePicker/datepickerLogic';
import { formatDateYmd } from '@/utils/dateFormat';

import type { PatientFormData } from '@pages/main/patient/types';

const jaPatientDatePickerFormat = parseFormat(JA_JP.format);

const SENTINEL_TO = '2100-12-31';
/** Legacy empty / invalid Y-m-d only — do not include '' (every string starts with ''). */
const EMPTY_DATE_PREFIXES = ['0000-00-00'];

export function isEmptyHistoryYmd(value: string | null | undefined): boolean {
  if (value == null) return true;
  const s = String(value).trim().slice(0, 10);
  if (s === '') return true;
  return EMPTY_DATE_PREFIXES.some(p => s.startsWith(p));
}

export const normalizeHistoryYmd = (s: string | null | undefined): string =>
  s == null || String(s).trim() === '' ? '' : String(s).trim().slice(0, 10);

export function subOneDayYmd(isoYmd: string): string {
  const d = new Date(`${isoYmd}T12:00:00`);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
}

/** BD 医療助成 (複数履歴): B vs min(保険証 資格取得) and max effective 有効期限 (open → 2100-12-31). */
export function healthInsuranceTimelineMinQualMaxEndYmd(
  cards: PatientFormData['health_insurance_cards'] | undefined,
): { minQ: string; maxE: string } | null {
  const list = cards ?? [];
  let minQ = '';
  let maxE = '1970-01-01';
  for (const c of list) {
    const q = String(c.qualification_date ?? '').trim().slice(0, 10);
    if (!q || isEmptyHistoryYmd(q)) continue;
    if (!minQ || q < minQ) minQ = q;
    const rawE = String(c.expiration_date ?? '').trim().slice(0, 10);
    const e = rawE && !isEmptyHistoryYmd(rawE) ? rawE : SENTINEL_TO;
    if (e > maxE) maxE = e;
  }
  if (!minQ) return null;
  return { minQ, maxE };
}

/** BD 3.1: null / 0000-00-00 → 2100-12-31 for sort (desc = this first). */
export function patientInfoEffectiveAvailableToYmd(availableTo: string | null | undefined): string {
  if (isEmptyHistoryYmd(availableTo)) return SENTINEL_TO;
  const s = String(availableTo).trim().slice(0, 10);
  const d = dayjs(s);
  return d.isValid() ? s : SENTINEL_TO;
}

/**
 * Display YYYY-MM-DD or blank when missing / sentinel.
 * Accepts API ISO datetimes, plain Y-m-d, and 和暦 strings (same as RA2NDatePicker).
 */
export function formatHistoryLabelYmd(value: string | null | undefined): string {
  if (value == null) return '';
  const trimmed = String(value).trim();
  if (trimmed === '') return '';
  if (isEmptyHistoryYmd(trimmed)) return '';

  const head = trimmed.slice(0, 10);
  if (head === SENTINEL_TO) return '';

  if (/^\d{4}-\d{2}-\d{2}$/.test(head)) {
    const d = dayjs(head);
    if (d.isValid()) return head;
  }

  const asFull = dayjs(trimmed);
  if (asFull.isValid()) {
    const ymd = asFull.format('YYYY-MM-DD');
    if (ymd === SENTINEL_TO) return '';
    return ymd;
  }

  try {
    const parsed = parseDate(trimmed, jaPatientDatePickerFormat);
    if (!Number.isNaN(parsed.getTime())) {
      const ymd = formatDateYmd(parsed);
      if (ymd === SENTINEL_TO) return '';
      return ymd;
    }
  } catch {
    /* invalid picker input */
  }

  return '';
}

export function addOneDayYmd(ymd: string): string {
  const d = dayjs(String(ymd).slice(0, 10));
  if (!d.isValid()) return '';
  return d.add(1, 'day').format('YYYY-MM-DD');
}

/** FROM = day after older record’s stored available_to; blank if older has no TO. */
export function patientInfoDateFromAfterOlder(
  olderAvailableTo: string | null | undefined,
): string {
  if (isEmptyHistoryYmd(olderAvailableTo)) return '';
  return addOneDayYmd(String(olderAvailableTo));
}

/** Legacy edit.blade: 8 fullwidth spaces when there is no older row to derive FROM (plain-text only). */
const LEGACY_HISTORY_FROM_PLACEHOLDER = '\u3000'.repeat(8);

/** Fixed column width for `YYYY-MM-DD` so 「から」aligns across dropdown rows (legacy UI). */
export const PATIENT_HISTORY_FROM_DATE_COLUMN_CH = 10;

export interface PatientInformationHistoryDropdownParts {
  fromYmd: string;
  toYmd: string;
  address: string;
}

/**
 * Structured patient 履歴 line for layout (fixed-width FROM column + から + TO + :address).
 */
export function getPatientInformationHistoryDropdownParts(
  info: { available_from?: string | null; available_to?: string | null; address1?: string | null },
  older: { available_to?: string | null } | undefined,
  fallbackFromYmd?: string,
): PatientInformationHistoryDropdownParts {
  const storedFromYmd = formatHistoryDropdownDate(info.available_from);
  const fromYmd = storedFromYmd || (older
    ? patientInfoDateFromAfterOlder(older.available_to)
    : formatHistoryDropdownDate(fallbackFromYmd));
  const toYmd = formatHistoryDropdownDate(info.available_to);
  const address = (info.address1 ?? '').trim();
  return {
    fromYmd: fromYmd && toYmd && fromYmd > toYmd ? '' : fromYmd,
    toYmd,
    address,
  };
}

/**
 * YYYY-MM-DD for history dropdown rows. Like {@link formatHistoryLabelYmd} but adds fallbacks
 * for ISO/datetime strings so labels are not blank when the API returns timestamps.
 */
export function formatHistoryDropdownDate(value: string | null | undefined): string {
  const primary = formatHistoryLabelYmd(value);
  if (primary) return primary;
  if (value == null || String(value).trim() === '') return '';
  const trimmed = String(value).trim();
  const head = trimmed.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(head)) {
    const d = dayjs(head);
    if (d.isValid() && !isEmptyHistoryYmd(head)) {
      if (head === SENTINEL_TO) return '';
      return head;
    }
  }
  const d = dayjs(trimmed);
  if (!d.isValid()) return '';
  const ymd = d.format('YYYY-MM-DD');
  if (ymd === SENTINEL_TO || isEmptyHistoryYmd(ymd)) return '';
  return ymd;
}

/**
 * Legacy A2 patient info history line:
 * `[day after older available_to | 全角8sp]から[available_to]:[address1]`
 * @see RA2/resources/views/patients/edit.blade.php (No236)
 */
export function formatPatientInformationHistoryDropdownLabel(
  info: { available_from?: string | null; available_to?: string | null; address1?: string | null },
  older: { available_to?: string | null } | undefined,
  fallbackFromYmd?: string,
): string {
  const { fromYmd, toYmd, address } = getPatientInformationHistoryDropdownParts(
    info,
    older,
    fallbackFromYmd
  );
  const fromPart = fromYmd || LEGACY_HISTORY_FROM_PLACEHOLDER;
  let body = `${fromPart}から${toYmd}`;
  if (address) body += `:${address}`;
  return body;
}

/** Same width as patient FROM column: 資格取得日 YYYY-MM-DD aligns 「から」across rows. */
export const HEALTH_INSURANCE_HISTORY_QUAL_DATE_COLUMN_CH = PATIENT_HISTORY_FROM_DATE_COLUMN_CH;

export interface InsuranceHistoryDropdownParts {
  qualYmd: string;
  expYmd: string;
}

export function getInsuranceHistoryDropdownParts(card: {
  qualification_date?: string | null;
  expiration_date?: string | null;
}): InsuranceHistoryDropdownParts {
  return {
    qualYmd: formatHistoryDropdownDate(card.qualification_date),
    expYmd: formatHistoryDropdownDate(card.expiration_date),
  };
}

/** Legacy: `qualification_date`から`expiration_date` (A2 health insurance history dropdown). */
export function formatInsuranceHistoryDropdownLabel(card: {
  qualification_date?: string | null;
  expiration_date?: string | null;
}): string {
  const { qualYmd, expYmd } = getInsuranceHistoryDropdownParts(card);
  if (qualYmd && expYmd) return `${qualYmd}から${expYmd}`;
  if (qualYmd) return `${qualYmd}から`;
  if (expYmd) return `から${expYmd}`;
  return '';
}

/** Align 適用開始日 column with 保険証 資格取得日 (same YYYY-MM-DD width in 履歴 dropdown). */
export const MEDICAL_AID_HISTORY_START_DATE_COLUMN_CH = HEALTH_INSURANCE_HISTORY_QUAL_DATE_COLUMN_CH;

export interface MedicalAidHistoryDropdownParts {
  startYmd: string;
  endYmd: string;
}

export function getMedicalAidHistoryDropdownParts(aid: {
  application_start_date?: string | null;
  application_end_date?: string | null;
}): MedicalAidHistoryDropdownParts {
  return {
    startYmd: formatHistoryDropdownDate(aid.application_start_date),
    endYmd: formatHistoryDropdownDate(aid.application_end_date),
  };
}

/** Legacy: `application_start_date`から`application_end_date` (A2 medical aid history dropdown). */
export function formatMedicalAidHistoryDropdownLabel(aid: {
  application_start_date?: string | null;
  application_end_date?: string | null;
}): string {
  const { startYmd, endYmd } = getMedicalAidHistoryDropdownParts(aid);
  if (startYmd && endYmd) return `${startYmd}から${endYmd}`;
  if (startYmd) return `${startYmd}から`;
  if (endYmd) return `から${endYmd}`;
  return '';
}

export function sortPatientInformationsForHistoryDropdown<
  T extends { available_to?: string | null },
>(rows: T[]): T[] {
  return [...rows].sort((a, b) =>
    patientInfoEffectiveAvailableToYmd(b.available_to).localeCompare(
      patientInfoEffectiveAvailableToYmd(a.available_to),
    ),
  );
}

export function sortInsuranceCardsForHistoryDropdown<
  T extends { qualification_date?: string | null },
>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const ta = a.qualification_date ? dayjs(a.qualification_date).valueOf() : 0;
    const tb = b.qualification_date ? dayjs(b.qualification_date).valueOf() : 0;
    return tb - ta;
  });
}

/**
 * A2 edit.blade 保険証: 履歴 is shown only in `@elseif` when
 * `$patient->healthInsuranceCards && $patient->healthInsuranceCards->first()->qualification_date`.
 * Eloquent `first()` without order ≈ lowest PK; not the same as “current first” in normalized API lists.
 */
export function healthInsuranceHistoryDropdownEligibleLegacy<
  T extends { id?: number | null; qualification_date?: string | null },
>(cards: T[] | undefined): boolean {
  if (!cards?.length) return false;
  const firstByPk = [...cards].sort(
    (a, b) => (Number(a.id) || 0) - (Number(b.id) || 0),
  )[0];
  return !isEmptyHistoryYmd(firstByPk?.qualification_date);
}

export function sortMedicalAidsForHistoryDropdown<
  T extends { application_start_date?: string | null },
>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const ta = a.application_start_date ? dayjs(a.application_start_date).valueOf() : 0;
    const tb = b.application_start_date ? dayjs(b.application_start_date).valueOf() : 0;
    return tb - ta;
  });
}

/**
 * A2 edit.blade.php 医療助成 履歴 `@elseif`:
 * `$patient->medicalAids && ($patient->medicalAid()->insurer_type_id != 1 || ($param['medical_cnt'] > 1))`.
 * Current row = `medicalAid()` (is_current); `medical_cnt` = number of rows (履歴が複数).
 * No958: 種類「無し」(id 1) かつ単一行のみでは履歴 UI を出さない。
 */
export function medicalAidHistoryHeaderEligibleLegacy<
  T extends { insurer_type_id?: number | null; is_current?: boolean },
>(aids: T[] | undefined): boolean {
  if (!aids?.length) return false;
  const current = aids.find(a => a.is_current) || aids[0];
  const typeId = current?.insurer_type_id;
  const multiple = aids.length > 1;
  return typeId !== 1 || multiple;
}

/** A2 CommonClass::getDefaultDate("to", $date) for Y-m-d strings. */
export function a2GetDefaultDateToYmd(dateYmd: string | null | undefined): string {
  const s = dateYmd == null ? '' : String(dateYmd).trim().slice(0, 10);
  if (s === '' || s.startsWith('0000-00-00')) {
    return SENTINEL_TO;
  }
  return s;
}

/** A2 PatientController::getAvailableError — PI `available_to` normalized for comparison. */
function normalizePatientInformationAvailableToForA2(
  value: string | null | undefined,
): string {
  return a2GetDefaultDateToYmd(value);
}

const A2_MSG_PI_AVAILABLE_TO_DUP = '他の受診者履歴の有効期限が重複しています。';

/**
 * Bulk modal: duplicate PI effective `available_to` vs 資格取得日 (A2 PI check only).
 * 保険証 intervals may contain X — BD split handles that server-side.
 */
export function validateA2BulkHistoryQualificationDate(
  qualificationDateYmd: string | null | undefined,
  patientData: PatientFormData | null | undefined,
): { ok: true } | { ok: false; message: string } {
  const qNorm = a2GetDefaultDateToYmd(
    qualificationDateYmd == null ? '' : String(qualificationDateYmd).trim(),
  );
  if (qNorm === SENTINEL_TO || isEmptyHistoryYmd(qualificationDateYmd)) {
    return { ok: false, message: '保険証資格取得日を入力してください。' };
  }

  const excludePiId = patientData?.current_patient_information?.id;
  const infos = patientData?.patient_informations ?? [];
  for (const pi of infos) {
    if (excludePiId != null && pi.id === excludePiId) {
      continue;
    }
    if (normalizePatientInformationAvailableToForA2(pi.available_to) === qNorm) {
      return { ok: false, message: A2_MSG_PI_AVAILABLE_TO_DUP };
    }
  }

  const qd = dayjs(qNorm);
  if (!qd.isValid()) {
    return { ok: false, message: '保険証資格取得日を入力してください。' };
  }

  return { ok: true };
}



/**
 * DB history row ids from RHF watch() or API may be number or numeric string.
 * A2 treats empty `health_insurance_card_id` / `patient_information_id` as “new draft”; anything coercible to a finite int is persisted.
 */
/**
 * A2 edit.blade `$all_change_bool`: patient has PI rows, HIC rows, and current (or first) card has 資格取得日.
 * Does not require `official` (仮登録 patients match A2).
 */
export function shouldShowA2BulkHistoryCreationButton(
  isEditMode: boolean,
  patientData: PatientFormData | null | undefined,
): boolean {
  if (!isEditMode || !patientData?.id) {
    return false;
  }
  const infos = patientData.patient_informations;
  if (!infos?.length) {
    return false;
  }
  const cards = patientData.health_insurance_cards;
  if (!cards?.length) {
    return false;
  }
  const card = cards.find(c => c.is_current) ?? cards[0];
  const q = card?.qualification_date;
  if (q == null || q === '') {
    return false;
  }
  return !isEmptyHistoryYmd(String(q));
}

export function coercePersistedHistoryRowId(value: unknown): number | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const t = value.trim();
    if (t === '') return undefined;
    const n = parseInt(t, 10);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

/** Coerce form/API 続柄 to number (Radio / JSON may use string). */
export function normalizeHealthInsuranceRelationshipId(
  relationshipId: unknown
): number | undefined {
  if (relationshipId === null || relationshipId === undefined) return undefined;
  if (typeof relationshipId === 'string' && relationshipId.trim() === '') return undefined;
  const n = typeof relationshipId === 'number' ? relationshipId : Number(relationshipId);
  return Number.isFinite(n) ? n : undefined;
}

/** 続柄「本人」(master id 1) のみ。67（本人）(3) は含めない（右カラム read-only 対象外）。 */
export function isInsuredSelfRelationship(relationshipId: unknown): boolean {
  const n = normalizeHealthInsuranceRelationshipId(relationshipId);
  return n === 1;
}
